import {ChangeEvent, useState} from 'react';
import {generatePath, useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {useCreateTransaction} from '@/api/payments';
import {getGqlErrorMessage, isAccessRestrictionError} from '@/api/request';
import {BillingType, PaymentProcess, PaymentTransactionStatus, PaymentType} from '@/enums';
import {useSession, useTossRequestBillingAuth} from '@/hooks';
import {useAirbridgeTracking} from '@/hooks/tracking';
import {ProductWithPriceInfo} from '@/interface';
import {paths, POLICY_LINKS} from '@/router';
import {Divider, Flexbox, FlexFullGrow, GrayRoundedBox, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {withComma} from '@/utils';
import {Button} from '../Button';
import {Checkbox, CheckboxItem, CheckboxLabel} from '../Checkbox';
import {toast} from '../HotToast';
import {Modal} from '../Modal';
import {Typography, typographyStyle} from '../Typography';

interface IProps {
  item: ProductWithPriceInfo;
  onClose?(): void;
}

const BILLING_PERIOD = {
  [BillingType.Annually]: '년',
  [BillingType.Monthly]: '개월',
  [BillingType.Daily]: '일',
};

export const OrderSheet = ({item, onClose}: IProps) => {
  const {finalPrice, vat, vatExtra} = item.priceInfo;
  const navigate = useNavigate();

  const {user} = useSession();
  const customerKey = user?.id;

  const {mutate: createTransaction, isPending} = useCreateTransaction();
  const requestBillingAuth = useTossRequestBillingAuth(customerKey);

  const [isAgree, setIsAgree] = useState<boolean>(false);

  const trackAirbridge = useAirbridgeTracking();

  const handleAgree = (e: ChangeEvent<HTMLInputElement>) => {
    const {checked} = e.target;
    setIsAgree(checked);
  };

  const onClick = () => {
    if (!item || !customerKey) {
      return;
    }

    createTransaction(
      {
        customerKey,
        product: item.id,
        method: '카드',
      },
      {
        onSuccess: (data) => {
          const transaction = data.result;

          if (
            transaction?.type === PaymentType.Billing &&
            transaction?.status === PaymentTransactionStatus.PREPARE
          ) {
            requestBillingAuth(transaction, item);
            trackAirbridge('airbridge.initiateCheckout', {
              action: '구독',
              label: item.name,
            });
            onClose?.();
          } else {
            navigate(
              {
                pathname: generatePath(paths.payments_process, {
                  transaction,
                  status: PaymentProcess.fail,
                }),
                search: `message=트랜잭션 생성 실패&code=${
                  transaction
                    ? `${transaction.type}/${transaction.status}`
                    : 'Not found transaction.'
                }`,
              },
              {replace: true}
            );
          }
        },
        onError: (error) => {
          // 이용 제한은 공통 에러 핸들러가 안내 모달을 띄운다.
          if (!isAccessRestrictionError(error)) {
            toast.error(getGqlErrorMessage(error));
          }
        },
      }
    );
  };

  return (
    <Modal title="주문 내역 확인" width={328} onClose={onClose}>
      <Root>
        <Typography variant="medium14" color={palette.gray600}>
          {item.name}을 결제하시겠어요?
        </Typography>
        <RoundedBox>
          <Flexbox>
            <Typography fontWeight={700}>정기 결제 금액</Typography>
            <FlexFullGrow />
            <Typography color={palette.gray600}>
              {withComma(vatExtra)}원
            </Typography>
          </Flexbox>
          <Flexbox>
            <Typography fontWeight={700}>VAT(10%)</Typography>
            <FlexFullGrow />
            <Typography color={palette.gray600}>{withComma(vat)}원</Typography>
          </Flexbox>
          <Divider />
          <Flexbox>
            <Typography fontWeight={700}>총 결제 금액</Typography>
            <FlexFullGrow />
            <Typography color={palette.gray600}>
              {withComma(finalPrice)}원
            </Typography>
          </Flexbox>
        </RoundedBox>
        <Typography variant="medium12" color={palette.red400} as="div">
          • {item.value}
          {BILLING_PERIOD[item.billingType]}
          마다 {withComma(finalPrice)}원이 자동으로 결제됩니다.
        </Typography>
        <CheckboxItem>
          <Checkbox
            name="payments-agree"
            checked={isAgree}
            onChange={handleAgree}
          />
          <CheckboxLabel>
            <a href={POLICY_LINKS.paid} target="_blank" rel="noreferrer">
              <u>
                <strong>유료 서비스 이용약관</strong>
              </u>
            </a>
            에 동의합니다.
          </CheckboxLabel>
        </CheckboxItem>
        <PaddingBox top={8}>
          <Button
            size="medium"
            color="secondary"
            variant="fill"
            fullWidth
            disabled={!isAgree || isPending}
            onClick={onClick}
          >
            {isPending ? '결제 요청 중...' : '결제하기'}
          </Button>
        </PaddingBox>
      </Root>
    </Modal>
  );
};

const Root = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RoundedBox = styled(GrayRoundedBox)`
  display: flex;
  flex-direction: column;
  gap: 16px 12px;
  ${{...typographyStyle.medium14}}
`;
